import { Trans } from '@lingui/react/macro';
import { Logo } from '../_general/Logo';

interface LoginBrandPanelProps {
  className?: string;
}

/** The blue brand panel shown on the left of the login page (desktop only). Also mirrored,
 * minus the logo/text, by the pre-mount boot splash markup in index.html. */
function LoginBrandPanel({ className = '' }: LoginBrandPanelProps) {
  return (
    <div className={`hidden md:flex flex-col items-center justify-center relative overflow-hidden bg-gradient-to-br from-primary to-blue-900 text-white px-10 py-14 ${className}`}>
      {/* Geometric shape texture — 3×3 grid cycling circle / square / triangle */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.15]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="login-shape-grid" x="0" y="0" width="84" height="84" patternUnits="userSpaceOnUse">
            {/* Row 0: circle · square · triangle */}
            <circle cx="14" cy="14" r="2.5" fill="none" stroke="white" strokeWidth="1.5" />
            <rect x="39.5" y="11.5" width="5" height="5" fill="none" stroke="white" strokeWidth="1.5" />
            <polygon points="70,11.5 72.2,15.25 67.8,15.25" fill="none" stroke="white" strokeWidth="1.5" />
            {/* Row 1: square · triangle · circle */}
            <rect x="11.5" y="39.5" width="5" height="5" fill="none" stroke="white" strokeWidth="1.5" />
            <polygon points="42,39.5 44.2,43.25 39.8,43.25" fill="none" stroke="white" strokeWidth="1.5" />
            <circle cx="70" cy="42" r="2.5" fill="none" stroke="white" strokeWidth="1.5" />
            {/* Row 2: triangle · circle · square */}
            <polygon points="14,67.5 16.2,71.25 11.8,71.25" fill="none" stroke="white" strokeWidth="1.5" />
            <circle cx="42" cy="70" r="2.5" fill="none" stroke="white" strokeWidth="1.5" />
            <rect x="67.5" y="67.5" width="5" height="5" fill="none" stroke="white" strokeWidth="1.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#login-shape-grid)" />
      </svg>

      {/* Animated blobs */}
      <div className="login-brand-blob--1 absolute -top-24 -left-24 w-80 h-80 rounded-full bg-white/15 blur-3xl" />
      <div className="login-brand-blob--2 absolute -bottom-28 -right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
      <div className="login-brand-blob--3 absolute top-1/3 right-1/4 w-48 h-48 rounded-full bg-white/10 blur-2xl" />

      <div className="relative z-10 flex flex-col items-center text-center gap-4">
        {/* Logo on a frosted circle so it reads on any gradient */}
        <div className="rounded-full bg-white/20 p-6 backdrop-blur-sm">
          <Logo width={80} height={80} />
        </div>

        {/* Org name */}
        <em className="text-md text-bolder tracking-widest text-white leading-loose uppercase">
          Oficina del Comisionado de
          <br />
          Instituciones Financieras
        </em>

        {/* Divider */}
        <div className="w-10 h-px bg-white/30" />

        {/* Tagline */}
        <em className="text-sm text-bold text-white/70 leading-relaxed max-w-[200px]">
          <Trans>Your portal for financial regulatory reporting and compliance.</Trans>
        </em>
      </div>
    </div>
  );
}

export default LoginBrandPanel;
